import { useEffect } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { selectRecipeIndex } from '../redux/recipeIndexSlice';
import { isOfferedTo } from '../utils/recipeLanguage';
import { trackEvent } from '../services/analyticsService';
import type { RecipeIndexEntry } from '../services/unifiedRecipeService';

type ReaderLanguage = 'en' | 'fr';

// Read the slice's field directly rather than importing `languageSlice`: that
// module pulls in expo-localization (ESM), which jest cannot parse, and the
// generator's suite imports the hook through useGenerateWeek.
const selectLanguage = (state: { language: { currentLanguage: ReaderLanguage } }) =>
  state.language.currentLanguage;

/**
 * The persisted recipe index, as the current reader may see it.
 *
 * The shared catalogue is loaded ungated (`unifiedRecipeService`) and gated
 * here, on read: the index is persisted and outlives a language switch, so a
 * gate at the loader would either serve the other language's list or have to
 * refetch on every switch. Memoised per language (a `Map`, not a single
 * last-args cache) so switching and switching back returns the same array and
 * re-renders nothing. A cook's own saved recipes are never hidden (#737).
 */
const perLanguage = new Map<ReaderLanguage, ReturnType<typeof makeSelector>>();

function makeSelector(language: ReaderLanguage) {
  return createSelector([selectRecipeIndex], (entries: RecipeIndexEntry[]) =>
    entries.filter(e => isOfferedTo(e, language)),
  );
}

export function selectReadableRecipeIndex(
  state: Parameters<typeof selectRecipeIndex>[0],
  language: ReaderLanguage,
): RecipeIndexEntry[] {
  let selector = perLanguage.get(language);
  if (!selector) {
    selector = makeSelector(language);
    perLanguage.set(language, selector);
  }
  return selector(state);
}

/** The fields the gate reads — the redux index and the library screen's entries both have them. */
type GatedEntry = { id: string; source?: string; originalLanguage?: string; languages?: string[] };

/** What the gate hid from a reader of `language`: the shared entries only, a sample of ten. */
export function hiddenReport(
  entries: GatedEntry[],
  language: ReaderLanguage,
): { hidden_count: number; sample_ids: string[] } {
  const hidden = entries.filter(e => !isOfferedTo(e, language));
  return { hidden_count: hidden.length, sample_ids: hidden.slice(0, 10).map(e => e.id) };
}

/**
 * One `recipe_hidden_untranslated` per surface, language and count — the
 * number the readiness dashboard watches. Fires again when the count moves
 * (the index reloaded, the sweep caught up), not on every render.
 */
export function useReportHiddenRecipes(
  entries: GatedEntry[],
  language: ReaderLanguage,
  surface: string,
): void {
  const { hidden_count, sample_ids } = hiddenReport(entries, language);
  const key = sample_ids.join('|');
  useEffect(() => {
    if (hidden_count === 0) return;
    trackEvent('recipe_hidden_untranslated', { surface, language, hidden_count, sample_ids: key.split('|') });
  }, [surface, language, hidden_count, key]);
}

export function useReadableRecipeIndex(): RecipeIndexEntry[] {
  const language = useSelector(selectLanguage);
  const all = useSelector(selectRecipeIndex);
  useReportHiddenRecipes(all, language, 'recipe-index');
  return useSelector((state: Parameters<typeof selectRecipeIndex>[0]) =>
    selectReadableRecipeIndex(state, language),
  );
}
